import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import dayjs from "dayjs";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Box, Stack } from "@mui/material";

// Components
import { userState, lastListCookieId, briefInfoType } from "../../interface/MainInterface";
import { HttpUnAuthApi } from "../../interface/unauth-api";
import { CommonUtils } from "../../utils/common_utils";
import LoadingCircle from "../../utils/LoadingCircle";
import { useWindowSize } from "../../utils/useWindowSize";
import PopAddScrap from "../scrap/PopAddScrap";
import BriefSkeleton from "./component/BriefSkeleton";

// CSS
import "./Brief.css";
import "swiper/css";

interface propsType {
  userState: userState;
}

const unAuthAPI = new HttpUnAuthApi();
const cUtils = new CommonUtils();

const Article = (props: propsType) => {
  const { pk, sk } = useParams();
  const windowSize = useWindowSize();
  const popScrapRef: any = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [isEnglish, setIsEnglish] = useState(true);
  const [briefInfo, setBreifInfo] = useState<briefInfoType | null>(null);

  const slideRef: any = useRef();
  const pcSlideRef: any =useRef();
  const [selectedImage, setSelectedImage] = useState<string>();
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1); // 현재 선택된 이미지의 인덱스

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [swiperInstance, setSwiperInstance] = useState<any>(null); // Swiper 인스턴스를 저장
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 열림 상태

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  const [isAtStart, setIsAtStart] = useState(true); // 슬라이드가 처음에 있는지
  const [isAtEnd, setIsAtEnd] = useState(false);    // 슬라이드가 끝에 있는지

  useEffect(() => {
    if (!props.userState.isAuth) window.location.href = "/";

    if (briefInfo?.image_list.length > 0) {
      setSelectedImage(briefInfo?.image_list[0]);
    }

    return () => {
      setIsLoading(false);
      setBreifInfo(null);
    };
  }, []);

  useEffect(() => {
    get_breif_info();
  }, [pk, sk]);

  // 슬라이드 변경 시 호출되는 함수
  const handleSlideChange = (swiper: any) => {
    const currentIndex = swiper.activeIndex;
    setIsAtStart(swiper.isBeginning);  // 슬라이드가 첫 번째면 true
    setIsAtEnd(swiper.isEnd);          // 슬라이드가 마지막이면 true
    setSelectedImage(briefInfo?.image_list[currentIndex]);

    // console.log("isBeginning ", swiper.isBeginning);
    // console.log("isEnd ", swiper.isEnd);
  };

  // 화살표 클릭 시 이미지 변경
  const handlePrevClick = () => {
    if (pcSlideRef.current) {
      pcSlideRef.current.swiper.slidePrev();
        handleSlideChange(pcSlideRef.current.swiper);

        if (selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
        }
    }
  };

  const handleNextClick = () => {
        if (pcSlideRef.current) {
          pcSlideRef.current.swiper.slideNext();
          handleSlideChange(pcSlideRef.current.swiper);
            
            if ( briefInfo?.image_list.length > 0 && selectedImageIndex < briefInfo?.image_list.length - 1) {
                setSelectedImageIndex(selectedImageIndex + 1);
            }
        }
  };

  // 전체 보기 버튼 클릭 시 모달 열기
  const handleViewAllClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
    // console.log("index " , index);
  };

  // 모달 외부 클릭 시 모달 닫기
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleImageLoad = () => {
    if (imgRef.current) {
      setImageWidth(imgRef.current.clientWidth); // 이미지가 로드된 후 너비 설정
      setImageHeight(imgRef.current.clientHeight);
    }
  };

  useEffect(() => {
    setSelectedImage(briefInfo?.image_list[selectedImageIndex]);
    // console.log("selectedImageIndex ", selectedImageIndex);
    // console.log(briefInfo?.image_list[selectedImageIndex]);
  }, [selectedImageIndex]);

  const get_breif_info = async () => {
    const param: any = {
      command: "get_brief_list_detail2",
      category: pk,
      briefSeq: sk,
    };

    setIsLoading(true);
    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      setBreifInfo(res.response.brief_info);
    } else {
      console.error("get_breif_info : ", res.response.error_msg);
    }
    setIsLoading(false);
  };

  return (
    <>
      {briefInfo ? (
        <section id="commWrap" className="brief">
          <article className="article-01">
            <div className="brief-wrap">
              <div className="brief-cont">
                <div className="fake-height-81" />
                <div className="brief-text">
                  <dl style={{ marginBottom: '24px' }}>
                    <dt>{briefInfo?.ai_title}</dt>
                  </dl>
                  <div className="brief-info">
                    <ul>
                      <li className="upload-date">{dayjs(briefInfo?.sk).format("YYYY-MM-DD HH:mm")}</li>
                    </ul>
                    <Stack direction='row' sx={{ display: "flex", alignItems: 'center' }} gap={2}>
                      {props.userState.isSuperAdmin === true && (
                        <span style={{ color : '#7A7A7A' }}>
                          <Stack direction='row' sx={{ display: "flex", alignItems: 'center', whiteSpace: "nowrap"}} gap={1} >
                            <i style={{ display : 'inline' }}>
                              <img 
                                src={cUtils.genRefSiteLogo(briefInfo.ref_site)} 
                                onClick={() => { window.open(briefInfo.ref_site, '_blank') }}
                                alt="" 
                              />
                            </i>
                          </Stack>
                        </span>
                      )}
                      <button
                        className="org-news-btn"
                        onClick={() => {
                          setIsEnglish(!isEnglish);
                        }}
                        style={{ width: windowSize.width > 375 ? "120px" : "100px"}}
                      >
                        번역 보기
                      </button>
                    </Stack>
                  </div>
                  {briefInfo?.image_list.length > 0 
                    ? (
                        <>
                        <div className="pc-img-wrap">
                          <Box sx={{ position: "relative" }}>
                            {/* 네비게이션 버튼 추가 */}
                            {briefInfo?.image_list.length > 1 &&
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: '16px',
                                    left: '16px',
                                    // right: '16px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '8px',
                                    // justifyContent: 'space-between',
                                    // transform: 'translateY(-50%)',
                                    zIndex: 8,
                                  }}
                                >
                                  <button 
                                    className="swiper-button-prev" 
                                    onClick={handlePrevClick}
                                    style={{ 
                                      position: "initial",
                                      backgroundColor: 'rgb(0, 0, 0)', 
                                      border: 'none', 
                                      borderRadius: '50px', 
                                      padding: '8px', 
                                      opacity: selectedImageIndex === 0 ? 0.5 : 1, 
                                      cursor: 'pointer', 
                                      width: '40px', 
                                      height: '40px', 
                                      transform: 'none',
                                    }}
                                  >
                                    <img 
                                      src="/images/icon-arr-prev.png" 
                                      alt="" 
                                      style={{ 
                                        minWidth: "8px",
                                        maxWidth: "10px",
                                        minHeight: "13px",
                                        maxHeight: "15px",
                                        width: "0.8vw", 
                                        height: "1.2vw" 
                                      }}
                                    />
                                  </button>
                                  <button 
                                    className="swiper-button-next" 
                                    onClick={handleNextClick} 
                                    style={{ 
                                      position: "initial",
                                      backgroundColor: 'rgb(0, 0, 0)', 
                                      border: 'none', 
                                      borderRadius: '50px', 
                                      padding: '8px', 
                                      opacity: selectedImageIndex === briefInfo?.image_list.length - 1 ? 0.5 : 1, 
                                      cursor: 'pointer', 
                                      width: '40px', 
                                      height: '40px', 
                                      left: "70px", 
                                      transform: 'none',
                                    }}
                                  >
                                    <img 
                                      src="/images/icon-arr-next.png" 
                                      alt="" 
                                      style={{ 
                                        minWidth: "8px",
                                        maxWidth: "10px",
                                        minHeight: "13px",
                                        maxHeight: "15px",
                                        width: "0.8vw", 
                                        height: "1.2vw" 
                                      }} 
                                    />
                                  </button>
                                </Box>
                            }
                          
                            <div className="article-img-wrap" style={{ justifyContent: "start", overflow: "none"}}>
                              {briefInfo?.image_list.length > 0 &&
                              <Swiper
                                ref={pcSlideRef}
                                slidesPerView={"auto"}
                                centeredSlides={false}  // 처음에는 슬라이드가 중앙에 오도록 설정
                                slideToClickedSlide={true}  // 클릭한 슬라이드로 이동
                                onSlideChange={handleSlideChange} // 슬라이드 변경 시 호출
                                style={{ width: "100%" }}
                              >
                              {briefInfo?.image_list.map((imgUrl: string, index: number) => (
                                <SwiperSlide key={`brief-list-${index}`} style={{ width: "238px", borderRadius: "0px", height: "100%", alignItems: "start", display: "block" }}>
                                  <div 
                                    key={`top-img-${index}`}
                                  >
                                    <Box
                                      // key={index}
                                      onClick={() => setSelectedImageIndex(index)}
                                      sx={{
                                        // margin: '0 8px',
                                        cursor: 'pointer',
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      <img
                                        loading="lazy"
                                        src={imgUrl}
                                        alt=""
                                        onError={(e) => {
                                          e.currentTarget.src = "/images/no-image.png";
                                          e.currentTarget.className = "noImage";
                                        }}
                                      />
                                    </Box>
                                    {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
                                    <button
                                      onClick={() => {handleViewAllClick(index);}}
                                      style={{
                                        position: 'absolute',
                                        top: '16px', // 위쪽 여백
                                        // left: '370px', // 오른쪽 여백
                                        right: '16px',
                                        // backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        border: 'none', 
                                        borderRadius: '5px',
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <img src="/images/btn-full-screen.png" style={{ width: "18px", height: "18px" }} alt="" />
                                    </button>
                                  </div>
                                </SwiperSlide>
                              ))}
                              {/* 왼쪽 그라데이션: 처음 슬라이드가 아니면 표시 */}
                              {!isAtStart && <div className="swiper-gradient-left" style={{ width: "200px", background: "linear-gradient(to right, white -20%, rgba(255, 255, 255, 0) 100%)" }}></div>}

                              {/* 오른쪽 그라데이션: 마지막 슬라이드가 아니면 표시 */}
                              {!isAtEnd && <div className="swiper-gradient-right" style={{ width: "200px", background: "linear-gradient(to left, white -20%, rgba(255, 255, 255, 0) 100%)" }}></div>}
                              </Swiper>
                            }
                            </div>
                          </Box>
                        </div>
                        <Box sx={{ position: "absolute", left: 0, right: 0 }} className="mobile-img-wrap">
                            <div className="main-slide-wrap mobile-img-wrap">
                            <div className="swiper_center">
                                <Swiper
                                ref={slideRef}
                                slidesPerView={"auto"}
                                centeredSlides={true}
                                loop
                                spaceBetween={windowSize.width > 1023 ? 25 : 12}
                                navigation={{
                                    nextEl: ".swiper-button-next",
                                    prevEl: ".swiper-button-prev",
                                }}
                                autoplay={{
                                    delay: 4000,
                                    disableOnInteraction: false,
                                }}
                                modules={[Autoplay]}
                                className="swiper-wrapper"
                                >
                                {briefInfo?.image_list.map((imgUrl: string, index: number) => (
                                    <SwiperSlide key={`brief-slide-${index}`} className="swiper-slide aitb-swiper-slide">
                                    <img
                                        loading="lazy"
                                        src={imgUrl}
                                        alt=""
                                        onError={(e) => {
                                        e.currentTarget.src = "/images/no-image.png";
                                        e.currentTarget.className = "noImage";
                                        }}
                                    />
                                    {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
                                    <button
                                      className="btn-full-screen"
                                      onClick={() => {handleViewAllClick(index)}}
                                      style={{
                                        position: 'absolute',
                                        top: '16px', // 위쪽 여백
                                        // left: '370px', // 오른쪽 여백
                                        right: '16px',
                                        // backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        border: 'none', 
                                        borderRadius: '5px',
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <img src="/images/btn-full-screen-20.png" alt="" />
                                    </button>
                                    </SwiperSlide>
                                ))}
                                </Swiper>
                            </div>
                            </div>
                        </Box>
                        <div className="main-slide-wrap mobile-img-wrap fake-height-320"></div>
                        </>
                    ) : (
                        <div className="pc-img-wrap">
                        {/* <div className="img-wrap">
                            <div key='top-image-0' style={{ backgroundColor: "#ddd" }}>
                            <img src="/images/no-image.png" className="noImage" />
                            </div>
                        </div> */}
                        </div>
                    )}
                  <dl>
                    {/* {briefInfo?.ai_introduction !== undefined &&
                      briefInfo?.ai_introduction !== "" &&
                      briefInfo?.ai_introduction !== null && (
                        <dd className="white-space-pre-line underline bold">{briefInfo?.ai_introduction + "\n\n"}</dd>
                      )} */}
                    {
                      isEnglish ? (
                        <dd className="white-space-pre-line">
                          <div className="markdown">
                          <ReactMarkdown  remarkPlugins={[remarkGfm]}>
                            {briefInfo?.ai_OriginContent}
                          </ReactMarkdown>
                          </div>
                        </dd>
                      ) : (
                        <div className="markdown">
                          <ReactMarkdown  remarkPlugins={[remarkGfm]}>
                            {briefInfo?.ai_KorOriginContent}
                          </ReactMarkdown>
                        </div>
                      )
                    }
                    {/* <dd className="white-space-pre-line">{isEnglish ? briefInfo?.ai_OriginContent : briefInfo?.ai_KorOriginContent}</dd> */}
                  </dl>
                </div>

                {/* <div className="brief-insight">
                  <dl>
                    <dt>
                      <span className="highlighter half">인사이트 도출</span>
                      <img src="/images/icon-insight.png" alt="" />
                    </dt>
                    {
                      briefInfo?.ai_insight.map((insight: any, index: number) => (
                        <dd className="white-space-pre-line">
                          {insight}
                        </dd>
                      ))
                    }
                  </dl>
                </div> */}

                {/* 여기서 스크랩은 정책 결정이 필요함 (어떤 키워드로 스크랩할지 모름) */}
                {/* <div className="brief-insight">
                <Box sx={{ display: "flex", justifyContent: "center", p: 1 }} className="scrap-info">
                  <button className="bg-black color-white" onClick={scrap}>
                    <img src="/images/icon-magazine-white.png" alt="" />
                    <i>
                      <span>스크랩하기</span>
                    </i>
                  </button>
                </Box>
              </div> */}

                {/* {alikeList.length > 0 && (
                  <div className="rel-brief">
                    <dl>
                      <dt>
                        비슷한 Brief 보기
                        <img src="/images/icon-look.png" alt="" />
                      </dt>
                      <dd>
                        {alikeList.map((data: briefInfoType, index: number) => (
                          <div
                            key={`alike-list-${index}`}
                            className="brief-box simple"
                            onClick={() => {
                              window.open(`/brief/${data.pk}/${data.sk}`);
                            }}
                          >
                            <div className="thumb-img">
                              <img
                                src={data.image_list[0]}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.src = "/images/no-image.png";
                                  e.currentTarget.className = "noImage";
                                }}
                              />
                            </div>
                            <div className="brief-summary">
                              <dl>
                                <dt>
                                  <p>{data.ai_title}</p>
                                </dt>
                                <dd>{dayjs(data.sk).format("YYYY-MM-DD HH:mm")}</dd>
                              </dl>
                            </div>
                          </div>
                        ))}
                      </dd>
                    </dl>
                  </div>
                )} */}
              </div>
            </div>
            <div className="breif-footer-margin"></div>
          </article>
          {/* {(queryParams.list === null || queryParams.result) && (
            <div id="bottomNav" className="nav-bar-wrap">
              <div className="nav-btns pc">
                {prevSeq !== "" ? (
                  <button className="prev-post" onClick={goPrev}>
                    ← 이전 Brief
                  </button>
                ) : (
                  <div></div>
                )}

                <button className="go-list" onClick={goList}>
                  <i className="list-icon"></i>리스트로 돌아가기
                </button>
                {nextSeq !== "" ? (
                  <button className="next-post" onClick={goNext}>
                    다음 Brief →
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
              <div className="nav-btns mobile">
                {prevSeq !== "" ? (
                  <button className="prev-post" onClick={goPrev}>
                    ←
                  </button>
                ) : (
                  <div></div>
                )}
                <button className="go-list" onClick={goList}>
                  <i className="list-icon"></i>리스트로 돌아가기
                </button>
                {nextSeq !== "" ? (
                  <button className="next-post" onClick={goNext}>
                    →
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          )} */}
        </section>
      ) : (
        <BriefSkeleton />
      )}
      
      {/* 전체 이미지 모달 */}
      {isModalOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={handleModalClose} // 모달 외부 클릭 시 닫기
          >
            <img
              src={selectedImage}
              ref={imgRef} 
              onLoad={handleImageLoad}
              alt="전체 이미지"
              style={{
                maxWidth: '85%',
                maxHeight: '85%',
              }}
            />
            
            {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
            <button
              onClick={handleModalClose}
              style={{
                position: 'absolute',
                top: `calc(50% - ${imageHeight / 2 - 24}px)`, // 위쪽 여백
                right: `calc(50% - ${imageWidth / 2 - 24}px)`, // 오른쪽 여백
                // backgroundColor: 'rgba(255, 255, 255, 0.8)',
                border: 'none',
                borderRadius: '5px',
                width: '32px',
                height: '32px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src="/images/btn-minimize-screen.png" alt="" />
            </button>
            
            {/* 모달 내 화살표 추가 */}
            {briefInfo?.image_list.length > 1 && 
              <>
              <Box
                sx={{
                  position: 'absolute',
                  left: `calc(50% - ${imageWidth / 2 + 40 + 35}px)`, // 왼쪽 여백
                  top: '50%', // 수직 중앙 정렬
                  transform: 'translateY(-50%)', // 수직 중앙 정렬을 위한 변환
                }}
              >
                <button
                  className="swiper-button-prev"
                  onClick={(e) => { e.stopPropagation(); handlePrevClick(); }}
                  style={{ backgroundColor: 'rgb(0, 0, 0)', border: 'none', borderRadius: '50px', padding: '8px', opacity: selectedImageIndex === 0 ? 0.5 : 1, cursor: 'pointer', width: '40px', height: '40px', transform: 'none' }}
                  disabled={selectedImageIndex === 0} // 첫 번째 이미지일 경우 비활성화
                >
                  <img src="/images/icon-arr-prev.png" alt="" style={{ width: "10px", height: "15px" }}/>
                </button>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  left: `calc(50% + ${imageWidth / 2 - 40 - 10}px)`, // 오른쪽 여백
                  top: '50%', // 수직 중앙 정렬
                  transform: 'translateY(-50%)', // 수직 중앙 정렬을 위한 변환
                }}
              >
                <button
                  className="swiper-button-next"
                  onClick={(e) => { e.stopPropagation(); handleNextClick(); }}
                  style={{ backgroundColor: 'rgb(0, 0, 0)', border: 'none', borderRadius: '50px', padding: '8px', opacity: selectedImageIndex === briefInfo?.image_list.length - 1 ? 0.5 : 1, cursor: 'pointer', width: '40px', height: '40px', left: "70px", transform: 'none' }}
                  disabled={selectedImageIndex === briefInfo?.image_list.length - 1} // 마지막 이미지일 경우 비활성화
                >
                  <img src="/images/icon-arr-next.png" alt="" style={{ width: "10px", height: "15px" }} />
                </button>
              </Box>
              </>
            }
          </Box>
        )}
      <LoadingCircle loading={isLoading} />
      <PopAddScrap ref={popScrapRef} userState={props.userState} selectedKeywords={[]} callback={(payload: any) => {}} />
    </>
  );
};

export default Article;
